import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {((event: MouseEvent) => void) | null} */
  #outsideClickListener = null;

  /** Invalidates in-flight slide-out completion when opening again (interruptible close). */
  #slideCloseGeneration = 0;

  /** `data-sticky-state` on `#header-component` before the root drawer was opened (restore on close). */
  #headerStickySnapshot = /** @type {string | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#bindOutsideClickToClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#unbindOutsideClickToClose();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open or finishing its close animation
   */
  get isOpen() {
    const details = this.refs.details;
    return details.classList.contains('menu-open') || details.dataset.drawerClosing === 'true';
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    // Use menu-open, not isOpen: during close animation drawerClosing is still set but the panel
    // is already sliding shut — isOpen stays true and would route to close() (no-op) instead of open().
    const shouldClose = this.refs.details.classList.contains('menu-open');
    return shouldClose ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    if (details === this.refs.details) {
      this.#slideCloseGeneration++;
      if (this.#headerStickySnapshot === null) {
        const header = document.querySelector('#header-component');
        this.#headerStickySnapshot = header?.getAttribute('data-sticky-state') ?? null;
      }
    }
    const summary = details.querySelector('summary');

    if (!summary) return;

    delete details.dataset.drawerClosing;
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    const isRootDrawer = details === this.refs.details;
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });

      if (isRootDrawer) {
        const header = document.querySelector('#header-component');
        if (header instanceof HTMLElement && !header.hasAttribute('sticky')) {
          header.dataset.stickyState = 'active';
        }
        window.dispatchEvent(new Event('scroll'));
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isRoot = details === this.refs.details;
    if (isRoot) {
      if (details.dataset.drawerClosing === 'true') return;
      if (!details.classList.contains('menu-open')) return;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (isRoot) {
      // Set before sticky/backdrop reconciliation so CSS can treat the drawer as “closing” while
      // `[open]:not([data-drawer-closing])` stays false — backdrop fades out immediately on close.
      details.dataset.drawerClosing = 'true';
    }

    if (isRoot) {
      const snap = this.#headerStickySnapshot;
      this.#headerStickySnapshot = null;
      const header = document.querySelector('#header-component');
      if (header instanceof HTMLElement) {
        if (header.hasAttribute('sticky')) {
          const hc =
            /** @type {HTMLElement & { reconcileStickyAfterDrawer?: (snap: string | null) => void }} */ (
              header
            );
          hc.reconcileStickyAfterDrawer?.(snap);
        } else {
          header.removeAttribute('data-sticky-state');
        }
      }
      window.dispatchEvent(new Event('scroll'));
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    if (isRoot) {
      // Keep native [open] until the panel finishes sliding out. Removing [open] immediately
      // makes the UA treat the details as closed and drops non-summary content from layout,
      // which kills the transform transition (abrupt close).
      //
      // Do not use onAnimationEnd(getAnimations) here: it resolves in the same turn if the
      // browser has not yet registered the exit transition. Wait for transform transitionend.
      const panel = drawer instanceof Element ? drawer : null;
      const generation = this.#slideCloseGeneration;
      afterSlideTransition(panel, () => {
        if (generation !== this.#slideCloseGeneration) return;
        delete details.dataset.drawerClosing;
        if (details.classList.contains('menu-open')) return;
        details.removeAttribute('open');
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        this.querySelectorAll('.sk-drawer-footer__localization-disclosure[open]').forEach((details) => {
          if (details instanceof HTMLDetailsElement) {
            details.open = false;
          }
        });
      });
      return;
    }

    details.removeAttribute('open');
    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        trapFocus(this.refs.details);
      },
      { subtree: false }
    );
  }

  /**
   * When the drawer is open, any click outside the panel closes it.
   * This mirrors the backdrop behavior and prevents header-specific desync.
   * @param {MouseEvent} event
   */
  #onOutsideClick = (event) => {
    if (!this.isOpen) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    if (this.refs.menuDrawer.contains(target)) return;

    event.preventDefault();
    event.stopPropagation();
    this.close();
  };

  #bindOutsideClickToClose() {
    if (this.#outsideClickListener) return;

    this.#outsideClickListener = this.#onOutsideClick;
    document.addEventListener('click', this.#outsideClickListener, { capture: true });
  }

  #unbindOutsideClickToClose() {
    if (!this.#outsideClickListener) return;

    document.removeEventListener('click', this.#outsideClickListener, { capture: true });
    this.#outsideClickListener = null;
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

class DesktopHeaderCascade extends HTMLElement {
  /** @type {((event: MouseEvent) => void) | null} */
  #outsideClickListener = null;

  /** @type {HTMLElement[]} */
  #sources = [];

  /** @type {boolean} */
  #bound = false;

  /** @type {boolean} */
  #ownsScrollLock = false;

  /** @type {(() => void) | null} */
  #resizeListener = null;

  /** @type {number | null} */
  #resizeRafId = null;

  /** @type {boolean} */
  #bandWidthLocked = false;

  /** @type {ResizeObserver | null} */
  #headerLayoutObserver = null;

  connectedCallback() {
    this.#sources = Array.from(
      this.querySelectorAll('[data-source-id][data-source-opens-drawer="true"]')
    ).filter((source) => source instanceof HTMLElement);

    if (!this.#bound) {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('mouseover', this.#onMouseOver);
      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('keydown', this.#onKeyDown);
      this.#bound = true;
    }

    this.#bindHeaderLayoutObserver();
    this.#syncInitialState();
    this.#updateCascadeLayout();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseover', this.#onMouseOver);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#bound = false;
    this.#setOpenState(false);
    this.#unbindResize();
    this.#unbindHeaderLayoutObserver();
    this.#sources = [];
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSources() {
    return this.#sources;
  }

  #bindOutsideClick() {
    if (this.#outsideClickListener) return;

    this.#outsideClickListener = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (this.contains(target)) return;

      this.#closeAllSources();
    };

    document.addEventListener('click', this.#outsideClickListener, { capture: true });
  }

  #unbindOutsideClick() {
    if (!this.#outsideClickListener) return;

    document.removeEventListener('click', this.#outsideClickListener, { capture: true });
    this.#outsideClickListener = null;
  }

  /**
   * @param {{ force?: boolean }} [options]
   */
  #updateBandWidth(options = {}) {
    if (this.#bandWidthLocked && !options.force) return;

    const row = this.querySelector('.desktop-header-cascade__row');
    if (!(row instanceof HTMLElement)) return;

    const bandWidth = Math.ceil(row.getBoundingClientRect().right);
    this.style.setProperty('--sk-desktop-cascade-band-width', `${bandWidth}px`);
    this.#bandWidthLocked = true;
  }

  #clearBandWidth() {
    this.style.removeProperty('--sk-desktop-cascade-band-width');
    this.#bandWidthLocked = false;
  }

  #bindResize() {
    if (this.#resizeListener) return;

    this.#resizeListener = () => {
      if (this.#resizeRafId !== null) return;

      this.#resizeRafId = requestAnimationFrame(() => {
        this.#resizeRafId = null;
        if (this.getAttribute('data-cascade-open') === 'true') {
          this.#updateBandWidth({ force: true });
        }
        this.#updateCascadeLayout();
      });
    };

    window.addEventListener('resize', this.#resizeListener);
  }

  #bindHeaderLayoutObserver() {
    if (this.#headerLayoutObserver) return;

    const headerGroup = document.getElementById('header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    this.#headerLayoutObserver = new ResizeObserver(() => {
      this.#updateCascadeLayout();
    });
    this.#headerLayoutObserver.observe(headerGroup);
  }

  #unbindHeaderLayoutObserver() {
    this.#headerLayoutObserver?.disconnect();
    this.#headerLayoutObserver = null;
  }

  /**
   * @param {string} variable
   * @param {number} fallback
   * @returns {number}
   */
  #readCssLengthPx(variable, fallback) {
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;width:var(${variable});`;
    this.append(probe);
    const px = probe.getBoundingClientRect().width;
    probe.remove();
    return px || fallback;
  }

  /**
   * Keeps drawer level-one links below header tabs with aligned horizontal inset.
   */
  #updateCascadeLayout() {
    const row = this.querySelector('.desktop-header-cascade__row');
    if (!(row instanceof HTMLElement)) return;

    const triggerLabels = [...row.querySelectorAll('.desktop-header-cascade__trigger-label')].filter(
      (label) => label.getBoundingClientRect().height > 0 && label.getBoundingClientRect().top > 0
    );
    const triggerLabel = triggerLabels[0] ?? row.querySelector('.desktop-header-cascade__trigger-label');
    if (!(triggerLabel instanceof HTMLElement)) return;

    const labelRect = triggerLabel.getBoundingClientRect();
    const menuGap = this.#readCssLengthPx('--desktop-header-cascade-menu-gap', 16);
    const menuTop = Math.round(labelRect.bottom + menuGap);
    const labelStyle = getComputedStyle(triggerLabel);
    const labelPaddingStart =
      Number.parseFloat(labelStyle.paddingInlineStart) || Number.parseFloat(labelStyle.paddingLeft) || 0;
    const itemInset = Math.round(labelRect.left + labelPaddingStart);

    this.style.setProperty('--desktop-header-cascade-menu-top', `${menuTop}px`);
    this.style.setProperty('--desktop-header-cascade-item-inset', `${itemInset}px`);
  }

  #unbindResize() {
    if (this.#resizeListener) {
      window.removeEventListener('resize', this.#resizeListener);
      this.#resizeListener = null;
    }

    if (this.#resizeRafId !== null) {
      cancelAnimationFrame(this.#resizeRafId);
      this.#resizeRafId = null;
    }
  }

  #syncInitialState() {
    const activeSource = this.#getSources().find(
      (source) => source.getAttribute('data-source-active') === 'true'
    );

    if (activeSource) {
      this.#activateSource(activeSource, { preserveLevelOne: true });
    } else {
      this.#closeAllSources();
    }
  }

  /**
   * @param {Element} element
   * @returns {HTMLElement | null}
   */
  #getContainingSource(element) {
    const source = element.closest('[data-source-id]');
    return source instanceof HTMLElement && this.#getSources().includes(source) ? source : null;
  }

  /**
   * @param {HTMLElement} source
   * @param {{ preserveLevelOne?: boolean }} [options]
   */
  #activateSource(source, options = {}) {
    if (source.getAttribute('data-source-opens-drawer') !== 'true') return;

    this.#getSources().forEach((item) => {
      const isActive = item === source;
      item.setAttribute('data-source-active', isActive ? 'true' : 'false');

      const trigger = item.querySelector('[data-source-trigger]');
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }

      const panel = item.querySelector('[data-source-panel]');
      if (panel instanceof HTMLElement) {
        panel.hidden = !isActive;
      }
    });

    this.#setOpenState(true);

    if (!options.preserveLevelOne) {
      this.#resetLevelOne(source);
    }
  }

  /**
   * @param {HTMLElement} source
   * @param {string} levelOneId
   */
  #activateLevelOne(source, levelOneId) {
    if (source.getAttribute('data-source-has-level-two') !== 'true') return;
    if (source.getAttribute('data-active-level-one') === levelOneId) return;

    const panel = source.querySelector(`[data-level-one-panel="${levelOneId}"]`);
    if (!(panel instanceof HTMLElement)) return;

    source.setAttribute('data-level-one-active', 'true');
    source.setAttribute('data-active-level-one', levelOneId);

    source.querySelectorAll('[data-level-one-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      const isActive = trigger.getAttribute('data-level-one-trigger') === levelOneId;
      trigger.classList.toggle('desktop-header-cascade__level-one-link--active', isActive);
    });

    source.querySelectorAll('[data-level-one-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = panel.getAttribute('data-level-one-panel') !== levelOneId;
    });
  }

  /**
   * @param {HTMLElement} source
   */
  #resetLevelOne(source) {
    source.setAttribute('data-level-one-active', 'false');
    source.removeAttribute('data-active-level-one');

    source.querySelectorAll('[data-level-one-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.classList.remove('desktop-header-cascade__level-one-link--active');
    });

    source.querySelectorAll('[data-level-one-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = true;
    });
  }

  #closeAllSources() {
    this.#getSources().forEach((source) => {
      source.setAttribute('data-source-active', 'false');
      this.#resetLevelOne(source);

      const trigger = source.querySelector('[data-source-trigger]');
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'false');
      }

      const panel = source.querySelector('[data-source-panel]');
      if (panel instanceof HTMLElement) {
        panel.hidden = true;
      }
    });
    this.#setOpenState(false);
  }

  /**
   * @param {boolean} isOpen
   */
  #setOpenState(isOpen) {
    const backdrop = this.querySelector('[data-cascade-backdrop]');
    if (backdrop instanceof HTMLElement) {
      backdrop.hidden = !isOpen;
    }

    const footer = this.querySelector('[data-cascade-footer]');
    if (footer instanceof HTMLElement) {
      footer.hidden = !isOpen;
    }

    if (!isOpen) {
      this.querySelectorAll('.sk-drawer-footer__localization-disclosure[open]').forEach((details) => {
        if (details instanceof HTMLDetailsElement) {
          details.open = false;
        }
      });
    }

    if (isOpen) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.#ownsScrollLock = true;
      if (!this.#bandWidthLocked) {
        this.#updateBandWidth({ force: true });
      }
      this.setAttribute('data-cascade-open', 'true');
      this.#bindResize();
      this.#bindOutsideClick();
      requestAnimationFrame(() => this.#updateCascadeLayout());
      return;
    }

    this.setAttribute('data-cascade-open', 'false');
    requestAnimationFrame(() => this.#updateCascadeLayout());
    this.#unbindResize();
    this.#clearBandWidth();
    this.#unbindOutsideClick();

    if (this.#ownsScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
      this.#ownsScrollLock = false;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-source-direct-link]')) return;

    if (target.closest('[data-cascade-backdrop]')) {
      event.preventDefault();
      this.#closeAllSources();
      return;
    }

    const levelOneTrigger = target.closest('[data-level-one-trigger]');
    if (levelOneTrigger instanceof HTMLElement) {
      event.preventDefault();
      this.#activateLevelOneFromTrigger(levelOneTrigger);
      return;
    }

    const trigger = target.closest('[data-source-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    const source = this.#getContainingSource(trigger);
    if (!source) return;

    event.preventDefault();

    const isActive = source.getAttribute('data-source-active') === 'true';
    if (isActive) {
      this.#closeAllSources();
      return;
    }

    this.#activateSource(source);
  };

  /**
   * @param {MouseEvent} event
   */
  #onMouseOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-level-one-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.#activateLevelOneFromTrigger(trigger);
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-level-one-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    this.#activateLevelOneFromTrigger(trigger);
  };

  /**
   * @param {HTMLElement} trigger
   */
  #activateLevelOneFromTrigger(trigger) {
    if (!trigger.hasAttribute('data-level-one-trigger')) return;

    const source = this.#getContainingSource(trigger);
    const levelOneId = trigger.getAttribute('data-level-one-trigger');

    if (!source || !levelOneId) return;
    if (source.getAttribute('data-source-has-level-two') !== 'true') return;

    this.#activateLevelOne(source, levelOneId);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.#closeAllSources();
  };
}

if (!customElements.get('desktop-header-cascade')) {
  customElements.define('desktop-header-cascade', DesktopHeaderCascade);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * Run callback after the drawer's slide-out transform transition, or soon if there is none.
 * @param {Element | null} element
 * @param {() => void} callback
 */
function afterSlideTransition(element, callback) {
  if (!element) {
    queueMicrotask(callback);
    return;
  }

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    element.removeEventListener('transitionend', onTransitionEnd);
    clearTimeout(fallbackId);
    callback();
  };

  /** @param {TransitionEvent} event */
  const onTransitionEnd = (event) => {
    if (event.target !== element) return;
    if (event.propertyName !== 'transform') return;
    finish();
  };

  element.addEventListener('transitionend', onTransitionEnd);
  const fallbackMs = 600;
  const fallbackId = setTimeout(finish, fallbackMs);

  // Allow two frames so the exit transition is registered before getAnimations() (mirrors open() timing).
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const running = element
        .getAnimations({ subtree: false })
        .some((anim) => anim.playState === 'running');
      if (!running) finish();
    });
  });
}
